import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  HomeIcon,
  CubeIcon,
  Squares2X2Icon,
  BuildingOfficeIcon,
  RectangleStackIcon,
  SparklesIcon,
  TagIcon,
  HeartIcon,
  GiftIcon,
  StarIcon,
  LightBulbIcon,
  SunIcon
} from '@heroicons/react/24/outline';

const CategoryNavigator = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  // Icon mapping fonksiyonu
  const getIconForCategory = (categoryName) => {
    const name = categoryName.toLowerCase();
    
    if (name.includes('yatak') || name.includes('bedroom')) return CubeIcon;
    if (name.includes('oturma') || name.includes('koltuk') || name.includes('living')) return HomeIcon;
    if (name.includes('yemek') || name.includes('dining')) return BuildingOfficeIcon;
    if (name.includes('mutfak') || name.includes('kitchen')) return Squares2X2Icon;
    if (name.includes('banyo') || name.includes('bath')) return Squares2X2Icon;
    if (name.includes('bahçe') || name.includes('garden')) return SunIcon;
    if (name.includes('ofis') || name.includes('office')) return BuildingOfficeIcon;
    if (name.includes('aydınlatma') || name.includes('light')) return LightBulbIcon;
    if (name.includes('duvar') || name.includes('wall')) return RectangleStackIcon;
    if (name.includes('sehpa') || name.includes('table')) return SunIcon;
    if (name.includes('genç') || name.includes('bebek') || name.includes('kid')) return StarIcon;
    if (name.includes('antika') || name.includes('vintage')) return SparklesIcon;
    if (name.includes('outlet') || name.includes('indirim')) return TagIcon;
    if (name.includes('düğün') || name.includes('wedding')) return HeartIcon;
    if (name.includes('köşe') || name.includes('corner')) return Squares2X2Icon;
    if (name.includes('bohem') || name.includes('bohemian')) return SparklesIcon;
    if (name.includes('tasarım') || name.includes('design')) return StarIcon;
    
    // Varsayılan icon
    return CubeIcon;
  };

  // Gradient mapping fonksiyonu
  const getGradientForCategory = (categoryName) => {
    const name = categoryName.toLowerCase();
    
    if (name.includes('yatak')) return { gradient: 'from-blue-400 to-blue-600', bgGradient: 'from-blue-50 to-blue-100' };
    if (name.includes('oturma') || name.includes('koltuk')) return { gradient: 'from-orange-400 to-yellow-500', bgGradient: 'from-orange-50 to-yellow-50' };
    if (name.includes('yemek')) return { gradient: 'from-pink-400 to-purple-500', bgGradient: 'from-pink-50 to-purple-50' };
    if (name.includes('mutfak')) return { gradient: 'from-emerald-400 to-teal-500', bgGradient: 'from-emerald-50 to-teal-50' };
    if (name.includes('banyo')) return { gradient: 'from-cyan-400 to-blue-500', bgGradient: 'from-cyan-50 to-blue-50' };
    if (name.includes('bahçe')) return { gradient: 'from-lime-400 to-green-500', bgGradient: 'from-lime-50 to-green-50' };
    if (name.includes('ofis')) return { gradient: 'from-slate-400 to-gray-600', bgGradient: 'from-slate-50 to-gray-50' };
    if (name.includes('aydınlatma')) return { gradient: 'from-yellow-400 to-orange-500', bgGradient: 'from-yellow-50 to-orange-50' };
    if (name.includes('düğün')) return { gradient: 'from-pink-400 to-rose-500', bgGradient: 'from-pink-50 to-rose-50' };
    if (name.includes('outlet') || name.includes('indirim')) return { gradient: 'from-red-400 to-red-600', bgGradient: 'from-red-50 to-red-100' };
    
    // Varsayılan gradient
    return { gradient: 'from-indigo-400 to-purple-500', bgGradient: 'from-indigo-50 to-purple-50' };
  };

  // Kategorileri veritabanından çek
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('categories')
          .select('id, name, description')
          .eq('is_active', true)
          .order('name');

        if (error) {
          console.error('Kategoriler yüklenirken hata:', error);
          return;
        }

        // Kategorileri icon ve gradient'ları ile eşleştir
        const categoriesWithIcons = data.map(category => {
          const { gradient, bgGradient } = getGradientForCategory(category.name);
          return {
            ...category,
            icon: getIconForCategory(category.name),
            gradient,
            bgGradient
          };
        });

        setCategories(categoriesWithIcons);
      } catch (error) {
        console.error('Kategori yükleme hatası:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Geçici olarak gizli - CategoryNavigator section'unu gösterme
  return null;
  
  /*
  Orijinal kod burada - aktif etmek için return null; satırını sil ve bu yorum bloğunu kaldır
  
  return (
    <div className="bg-gradient-to-r from-gray-50 via-white to-gray-50 border-b border-gray-100 py-6">
      <div className="relative w-full">
        Scroll Arrows - Sadece kategoriler çoksa göster
        {!loading && categories.length > 6 && (
          <>
            Sol Ok
            <motion.button
              onClick={scrollLeft}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-all duration-200"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
            </motion.button>

            Sağ Ok
            <motion.button
              onClick={scrollRight}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-all duration-200"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ChevronRightIcon className="h-6 w-6 text-gray-600" />
            </motion.button>
          </>
        )}

        Kategoriler
        <div 
          ref={scrollRef}
          className={`flex gap-6 overflow-x-auto scrollbar-hide px-16 py-2 ${categories.length <= 6 ? 'justify-center' : 'justify-start'}`}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {loading ? (
            Loading skeleton
            Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="flex-none">
                <div className="flex flex-col items-center space-y-3 min-w-[100px]">
                  <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse"></div>
                  <div className="w-20 h-3 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            ))
          ) : categories.length > 0 ? (
            categories.map((category) => (
              <Link 
                to={`/products?category=${encodeURIComponent(category.name)}`} 
                key={category.id}
                className="flex-none"
              >
                <motion.div
                  className="group cursor-pointer"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="flex flex-col items-center space-y-3 min-w-[100px]">
                    İkon Dairesi
                    <div className={`relative w-16 h-16 rounded-full bg-gradient-to-br ${category.gradient} shadow-lg group-hover:shadow-xl transition-all duration-300`}>
                      <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${category.bgGradient} opacity-0 group-hover:opacity-20 transition-opacity duration-300`}></div>
                      <category.icon className="absolute inset-0 m-auto h-8 w-8 text-white" />
                      
                      Hover Ring Effect
                      <div className="absolute -inset-1 rounded-full border-2 border-transparent group-hover:border-gray-200 transition-all duration-300"></div>
                    </div>

                    Kategori Adı
                    <p className="text-xs font-medium text-gray-700 text-center leading-tight group-hover:text-gray-900 transition-colors duration-200 max-w-[90px]">
                      {category.name}
                    </p>
                  </div>
                </motion.div>
              </Link>
            ))
          ) : (
            Boş durum
            <div className="flex items-center justify-center w-full py-8">
              <div className="text-center">
                <TagIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Henüz kategori bulunmuyor</p>
              </div>
            </div>
          )}
        </div>
      </div>

      CSS
      <style>
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      </style>
    </div>
  );
  */
};

export default CategoryNavigator; 